import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationApi,
  NotificationSettings as NotificationSettingsType,
  NotificationEventType,
  NotificationEventTypeLabels,
  UpdateNotificationSettingsDto,
} from '../services/notificationApi';

const NotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<NotificationSettingsType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [testDialog, setTestDialog] = useState<{ open: boolean; eventType: NotificationEventType | null }>({
    open: false,
    eventType: null,
  });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await notificationApi.getAllSettings();
      setSettings(data);
      // 활성화된 항목들은 기본 확장
      const enabledItems = new Set(data.filter(s => s.is_enabled).map(s => s.event_type));
      setExpandedItems(enabledItems);
    } catch (error) {
      setMessage({ type: 'error', text: '알림 설정을 불러오는데 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (eventType: NotificationEventType, updates: UpdateNotificationSettingsDto) => {
    try {
      setSaving(eventType);
      await notificationApi.updateSettings(eventType, updates);
      setSettings(prev => prev.map(s =>
        s.event_type === eventType ? { ...s, ...updates } : s
      ));
      setMessage({ type: 'success', text: '설정이 저장되었습니다.' });
    } catch (error) {
      setMessage({ type: 'error', text: '설정 저장에 실패했습니다.' });
    } finally {
      setSaving(null);
    }
  };

  const handleToggleEnabled = async (setting: NotificationSettingsType) => {
    await handleUpdateSettings(setting.event_type, { is_enabled: !setting.is_enabled });
  };

  const handleSendTest = async () => {
    if (!testDialog.eventType || !testEmail) return;
    try {
      setSendingTest(true);
      await notificationApi.sendTestNotification(testDialog.eventType, testEmail);
      setMessage({ type: 'success', text: '테스트 메일이 발송되었습니다.' });
      setTestDialog({ open: false, eventType: null });
      setTestEmail('');
    } catch (error) {
      setMessage({ type: 'error', text: '테스트 메일 발송에 실패했습니다.' });
    } finally {
      setSendingTest(false);
    }
  };

  const toggleExpand = (eventType: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventType)) {
        newSet.delete(eventType);
      } else {
        newSet.add(eventType);
      }
      return newSet;
    });
  };

  const getEventTypeIcon = (eventType: NotificationEventType) => {
    switch (eventType) {
      case NotificationEventType.LEAVE_REQUEST:
      case NotificationEventType.LEAVE_APPROVED:
      case NotificationEventType.LEAVE_REJECTED:
        return '🏖️';
      case NotificationEventType.PPE_CREATED:
      case NotificationEventType.PPE_UPDATED:
        return '📊';
      case NotificationEventType.SALARY_TRANSFER:
        return '💰';
      case NotificationEventType.CARD_USAGE:
      case NotificationEventType.CARD_TRANSFER:
        return '💳';
      default:
        return '🔔';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🔔</span>
          <h1 className="text-xl font-bold text-gray-800">알림 설정</h1>
        </div>
        <button
          onClick={() => navigate('/notification-history')}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          발송 내역
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </div>
      )}

      {/* Info Alert */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
        각 이벤트별로 알림 발송 설정을 관리할 수 있습니다. SMTP 설정이 완료되어야 이메일이 정상적으로 발송됩니다.
      </div>

      {/* Settings List */}
      <div className="space-y-3">
        {settings.map((setting) => (
          <div key={setting.event_type} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleExpand(setting.event_type)}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{getEventTypeIcon(setting.event_type)}</span>
                <span className="font-medium text-gray-800">
                  {NotificationEventTypeLabels[setting.event_type] || setting.event_type}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleEnabled(setting);
                  }}
                  className={`px-3 py-1 text-xs font-medium rounded-full ${
                    setting.is_enabled
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {setting.is_enabled ? 'ON' : 'OFF'}
                </button>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform ${expandedItems.has(setting.event_type) ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Content */}
            {expandedItems.has(setting.event_type) && (
              <div className={`px-4 pb-4 border-t border-gray-100 ${!setting.is_enabled ? 'opacity-50' : ''}`}>
                <div className="pt-4 space-y-4">
                  {/* Toggle */}
                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={setting.is_enabled}
                        onChange={() => handleToggleEnabled(setting)}
                        className="sr-only peer"
                        disabled={saving === setting.event_type}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                    <span className="text-sm text-gray-600">알림 활성화</span>
                  </div>

                  {/* Email Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">수신자 이메일</label>
                    <input
                      type="text"
                      value={setting.recipient_emails || ''}
                      onChange={(e) => setSettings(prev => prev.map(s =>
                        s.event_type === setting.event_type ? { ...s, recipient_emails: e.target.value } : s
                      ))}
                      onBlur={(e) => handleUpdateSettings(setting.event_type, { recipient_emails: e.target.value })}
                      placeholder="이메일 주소를 쉼표로 구분하여 입력"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={saving === setting.event_type || !setting.is_enabled}
                    />
                    <p className="mt-1 text-xs text-gray-500">여러 이메일은 쉼표(,)로 구분</p>
                  </div>

                  {/* Subject Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메일 제목</label>
                    <input
                      type="text"
                      value={setting.email_subject || ''}
                      onChange={(e) => setSettings(prev => prev.map(s =>
                        s.event_type === setting.event_type ? { ...s, email_subject: e.target.value } : s
                      ))}
                      onBlur={(e) => handleUpdateSettings(setting.event_type, { email_subject: e.target.value })}
                      placeholder="메일 제목"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={saving === setting.event_type || !setting.is_enabled}
                    />
                  </div>

                  {/* Send Mode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">발송 방식</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`sendMode-${setting.event_type}`}
                          checked={setting.send_immediately}
                          onChange={() => handleUpdateSettings(setting.event_type, { send_immediately: true })}
                          className="w-4 h-4 text-indigo-600"
                          disabled={saving === setting.event_type || !setting.is_enabled}
                        />
                        <span className="text-sm text-gray-600">즉시 발송</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`sendMode-${setting.event_type}`}
                          checked={!setting.send_immediately}
                          onChange={() => handleUpdateSettings(setting.event_type, { send_immediately: false })}
                          className="w-4 h-4 text-indigo-600"
                          disabled={saving === setting.event_type || !setting.is_enabled}
                        />
                        <span className="text-sm text-gray-600">일괄 발송 (매일 09:00)</span>
                      </label>
                    </div>
                  </div>

                  {/* Test Button */}
                  <div className="pt-2 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setTestDialog({ open: true, eventType: setting.event_type });
                        setTestEmail(setting.recipient_emails?.split(',')[0]?.trim() || '');
                      }}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50"
                      disabled={saving === setting.event_type || !setting.is_enabled}
                    >
                      테스트 발송
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Test Email Modal */}
      {testDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span>📧</span> 테스트 메일 발송
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              입력한 이메일 주소로 테스트 알림 메일을 발송합니다.
            </p>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setTestDialog({ open: false, eventType: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleSendTest}
                disabled={!testEmail || sendingTest}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {sendingTest ? '발송 중...' : '발송'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
